import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Empty, Input, Message, Modal, Select, Space, Table, Typography } from "@arco-design/web-react";
import { IconEdit } from "@arco-design/web-react/icon";
import Content from "../Layout/Content";
import SummaryCard from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi, OverwriteApi } from "../Api/AuthenticatedApi";
import CustomFieldInputs, { missingRequiredFields, formatErrors } from "../Components/CustomFields/CustomFieldInputs";
import useRecordTypes from "../Components/CustomFields/useRecordTypes";

const { Text } = Typography;

export default function CompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDomain, setEditDomain] = useState("");
  const [cfDefs, setCfDefs] = useState([]);
  const [editCustomFields, setEditCustomFields] = useState({});
  const [editTypeId, setEditTypeId] = useState("");
  const { types } = useRecordTypes("company");

  useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/custom-fields/?entity_type=company",
      (data) => setCfDefs(data || []), () => {}, () => {});
  }, [getAccessTokenSilently]);

  const base = "/api/companies/" + companyId;

  const fetchContacts = useCallback(() => {
    FetchApi(getAccessTokenSilently, base + "/contacts",
      (data) => { setContacts(data || []); setLoading(false); },
      () => navigate("/login"),
      () => { setLoading(false); Message.error("Failed to load contacts"); });
  }, [getAccessTokenSilently, navigate, base]);

  const fetchCompany = useCallback(() => {
    FetchApi(getAccessTokenSilently, base,
      (data) => setCompany(data),
      () => navigate("/login"),
      () => Message.error("Failed to load company"));
  }, [getAccessTokenSilently, navigate, base]);

  useEffect(() => { fetchCompany(); fetchContacts(); }, [fetchCompany, fetchContacts]);

  const openEdit = () => {
    if (!company) return;
    setEditName(company.name || "");
    setEditDomain(company.domain || "");
    setEditCustomFields(company.custom_fields || {});
    setEditTypeId(company.record_type_id || "");
    setEditOpen(true);
  };

  const saveEdit = () => {
    if (!editName.trim()) { Message.warning("Name is required"); return; }
    const missing = missingRequiredFields(cfDefs, editCustomFields, editTypeId);
    if (missing.length) { Message.warning("Required: " + missing.join(", ")); return; }
    const badFormat = formatErrors(cfDefs, editCustomFields, editTypeId);
    if (badFormat.length) { Message.warning(badFormat.join("; ")); return; }
    OverwriteApi(getAccessTokenSilently, base,
      (updated) => { setCompany(updated); setEditOpen(false); Message.success("Company updated"); },
      () => navigate("/login"),
      (err) => Message.error((err && err.message) || "Failed to update company"),
      { name: editName.trim(), domain: editDomain.trim(), record_type_id: editTypeId || null, custom_fields: editCustomFields });
  };

  const unlink = (e, contact) => {
    e.stopPropagation();
    OverwriteApi(getAccessTokenSilently, "/api/contacts/" + contact.id + "/company",
      () => { Message.success("Contact unlinked"); fetchContacts(); fetchCompany(); },
      () => navigate("/login"),
      () => Message.error("Failed to unlink contact"),
      { company_id: "" });
  };

  const columns = [
    { title: "Name", dataIndex: "name", render: (n, c) => n || c.email || "—" },
    { title: "Email", dataIndex: "email", render: (em) => <Text type="secondary">{em}</Text> },
    {
      title: "",
      width: 90,
      render: (_, c) => <Button size="small" type="text" onClick={(e) => unlink(e, c)}>Unlink</Button>,
    },
  ];

  return (
    <Content>
      <SimpleBreadcrumbs
        elements={[{ name: "Companies", path: "/companies" }, { name: (company && company.name) || "Company" }]}
      />

      <div style={{ display: "flex", alignItems: "center", padding: "8px 8px" }}>
        <div style={{ flexGrow: 1 }}>
          {company && (
            <Text type="secondary">
              {company.domain ? <>{company.domain} · </> : null}{company.contact_count} contact(s)
            </Text>
          )}
        </div>
        <Button icon={<IconEdit />} onClick={openEdit}>Edit</Button>
      </div>

      <SummaryCard title="Contacts" subTitle="People at this company">
        <Table
          size="small"
          rowKey="id"
          columns={columns}
          data={contacts}
          loading={loading}
          pagination={false}
          noDataElement={
            <Empty description="No contacts linked yet — link a contact from its profile (the Company field on the contact editor).">
              <Button type="text" style={{ marginTop: 8 }} onClick={() => navigate("/contacts")}>Go to Contacts</Button>
            </Empty>
          }
          onRow={(row) => ({
            style: { cursor: "pointer" },
            onClick: () => navigate("/contacts/" + row.id),
          })}
        />
      </SummaryCard>

      <Modal
        title="Edit company"
        visible={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={saveEdit}
        okText="Save"
        unmountOnExit
      >
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Name</Text></div>
            <Input value={editName} onChange={setEditName} />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Domain</Text></div>
            <Input value={editDomain} onChange={setEditDomain} />
          </div>
          {types.length > 0 && (
            <div>
              <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Type</Text></div>
              <Select
                allowClear
                placeholder="No type"
                style={{ width: "100%" }}
                value={editTypeId || undefined}
                onChange={(v) => setEditTypeId(v || "")}
                options={types.map((t) => ({ label: t.name, value: t.id }))}
              />
            </div>
          )}
          <CustomFieldInputs definitions={cfDefs} values={editCustomFields} typeId={editTypeId} onChange={setEditCustomFields} />
        </Space>
      </Modal>
    </Content>
  );
}
